import { useEffect, useSyncExternalStore } from "react";
export interface Appearance { theme: "system" | "light" | "dark"; fontSize: number; density: "comfortable" | "compact"; wrapCode: boolean }
const key = "swarm.workbench.appearance";
const defaults: Appearance = { theme: "system", fontSize: 15, density: "comfortable", wrapCode: false };
let volatile: string | undefined;
function snapshot() {
  if (volatile !== undefined) return volatile;
  try { return localStorage.getItem(key) ?? "{}"; } catch { return "{}"; }
}
function parse(value: string): Appearance {
  try {
    const data = JSON.parse(value);
    return { theme: ["system", "light", "dark"].includes(data?.theme) ? data.theme : "system", fontSize: Number.isInteger(data?.fontSize) && data.fontSize >= 14 && data.fontSize <= 18 ? data.fontSize : 15, density: data?.density === "compact" ? "compact" : "comfortable", wrapCode: data?.wrapCode === true };
  } catch { return defaults; }
}
function subscribe(notify: () => void) {
  window.addEventListener("swarm-appearance", notify); window.addEventListener("storage", notify);
  return () => { window.removeEventListener("swarm-appearance", notify); window.removeEventListener("storage", notify); };
}
export function useAppearance() {
  const raw = useSyncExternalStore(subscribe, snapshot, () => "{}");
  return { value: parse(raw), saved: volatile === undefined, save: (patch: Partial<Appearance>) => {
    // Merge at invocation time so controls in another mounted code block do not
    // overwrite newer theme/font changes with an old render's preferences.
    const next = JSON.stringify({ ...parse(snapshot()), ...patch });
    let saved = true;
    try { localStorage.setItem(key, next); volatile = undefined; }
    catch { volatile = next; saved = false; }
    window.dispatchEvent(new Event("swarm-appearance"));
    return saved;
  } };
}
export function useApplyAppearance() {
  const { value } = useAppearance();
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      document.documentElement.dataset.theme = value.theme === "system" ? (media.matches ? "dark" : "light") : value.theme;
      document.documentElement.dataset.density = value.density;
      document.documentElement.style.setProperty("--chat-font-size", value.fontSize + "px");
    };
    apply(); media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [value.theme, value.fontSize, value.density]);
}
